import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { formatApiError } from "@/lib/api";
import { GaugeCircle, Sparkles } from "lucide-react";

export default function Signup() {
  const { signup } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({
    full_name: "",
    org_name: "",
    country: "UK",
    email: "",
    password: "",
  });
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const onChange = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      await signup(form);
      nav("/onboarding");
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-surface flex flex-col justify-center items-center p-5" data-testid="signup-page">
      <Link to="/" className="flex items-center gap-2 mb-8">
        <div className="w-9 h-9 rounded-lg bg-navy-900 flex items-center justify-center">
          <GaugeCircle className="w-5 h-5 text-orange-500" />
        </div>
        <span className="font-bold text-lg tracking-tight">EntryBook</span>
      </Link>
      <div className="w-full max-w-md bg-white rounded-2xl shadow-card border border-navy-100 p-8" data-testid="signup-card">
        <div className="flex items-center gap-2 eb-overline mb-2">
          <Sparkles className="w-3 h-3" /> Free 14-day trial
        </div>
        <h1 className="text-2xl font-semibold mb-6">Create your workspace</h1>
        <form onSubmit={submit} className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <label className="eb-label">Your name</label>
              <input required value={form.full_name} onChange={onChange("full_name")} className="eb-input" data-testid="signup-name-input" />
            </div>
            <div>
              <label className="eb-label">Organisation</label>
              <input required value={form.org_name} onChange={onChange("org_name")} className="eb-input" data-testid="signup-org-input" placeholder="Acme Ltd" />
            </div>
            <div>
              <label className="eb-label">Country</label>
              <select value={form.country} onChange={onChange("country")} className="eb-input" data-testid="signup-country-select">
                <option value="UK">United Kingdom</option>
                <option value="IE">Ireland</option>
              </select>
            </div>
            <div className="col-span-2">
              <label className="eb-label">Work email</label>
              <input required type="email" value={form.email} onChange={onChange("email")} className="eb-input" data-testid="signup-email-input" placeholder="you@company.co.uk" />
            </div>
            <div className="col-span-2">
              <label className="eb-label">Password</label>
              <input required type="password" minLength={8} value={form.password} onChange={onChange("password")} className="eb-input" data-testid="signup-password-input" placeholder="At least 8 characters" />
            </div>
          </div>
          {err && (
            <div className="rounded-md bg-danger/10 border border-danger/30 text-danger text-sm px-3 py-2" data-testid="signup-error">
              {err}
            </div>
          )}
          <button type="submit" disabled={loading} className="btn-accent w-full" data-testid="signup-submit-btn">
            {loading ? "Creating…" : "Create workspace"}
          </button>
        </form>
        <div className="mt-6 text-sm text-navy-600 text-center">
          Already have an account?{" "}
          <Link to="/login" className="font-semibold text-orange-600 hover:underline" data-testid="go-to-login">
            Log in
          </Link>
        </div>
      </div>
    </div>
  );
}
